import React, { useEffect, useMemo, useState } from "react";
import api from "../../../axiosConfig";

const roleLabel = (name) => ({ admin: "Administrator", user: "Utilizator" }[name] || name);
const groupLabel = (name) => ({ ADMIN: "Administrare", ALL: "Acces general", NOM: "Nomenclatoare", INTRARE: "Intrări", IESIRE: "Ieșiri", BON: "Bonuri", NC: "Note contabile", RAPOARTE: "Rapoarte", JURNAL: "Jurnale", BALANTA: "Balanță", STOC: "Stocuri", CONTACT: "Parteneri", COMENZI: "Comenzi", SANTIER: "Șantier", SALARII: "Salarii", SETARI: "Setări", CONTOR: "Contoare", ISTORIC: "Istoric", MISC: "Alte drepturi" }[name] || name);

function groupFromCode(code) {
  if (!code) return "MISC";
  const i = code.indexOf("_");
  return (i > 0 ? code.slice(0, i) : "MISC").toUpperCase();
}

function overridesToMap(overridesArr) {
  // overrides: [{permission_id, granted}]
  const m = {};
  for (const o of overridesArr || []) {
    m[String(o.permission_id)] = !!o.granted;
  }
  return m;
}

function mapToOverrides(m) {
  // map: { [permission_id]: true|false } only (inherit missing)
  return Object.entries(m).map(([pid, gr]) => ({
    permission_id: Number(pid),
    granted: !!gr,
  }));
}

export default function UserRightsPage() {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setL] = useState(false);
  const [err, setErr] = useState("");

  const [roles, setRoles] = useState([]); // [{id,name}]
  const [perms, setPerms] = useState([]); // [{id,code,description,group?}]

  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  // edit state
  const [edit, setEdit] = useState(null);
  // edit = { id, username, is_active, role_ids:[], role_perm_ids:[], effective_perm_ids:[], overridesMap:{} }

  // ---------------- BRANCH RIGHTS ----------------
  const [branches, setBranches] = useState([]); // [{id, code, name ...}]
  const [branchIds, setBranchIds] = useState([]); // [int]
  const [defaultBranchId, setDefaultBranchId] = useState(null); // int|null
  const [savingBranches, setSavingBranches] = useState(false);
  const [branchesReady, setBranchesReady] = useState(false);
  const [branchListReady, setBranchListReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [baseReady, setBaseReady] = useState(false);

  const [permQ, setPermQ] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState({}); // group => bool collapsed?

  async function loadBase() {
    const [rRoles, rPerms] = await Promise.all([
      api.get("/auth/admin/roles"),
      api.get("/auth/admin/perms"),
    ]);
    setRoles(rRoles.data || []);
    setBaseReady(true);
    // dacă backend-ul nu returnează "group", îl calculăm aici
    const permsRaw = rPerms.data || [];
    setPerms(
      permsRaw.map((p) => ({
        ...p,
        description: p.code === "ALL_BRANCHES" ? "Acces global la toate unitățile" : (p.description || "").replace(" (branches)", ""),
        group: p.group || groupFromCode(p.code),
      })),
    );
  }

  async function loadUsers() {
    setErr("");
    setL(true);
    try {
      const res = await api.get("/auth/admin/users", {
        params: { q, skip: 0, limit: 100 },
      });
      setUsers(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      setErr(e.response?.data?.detail || "Eroare la încărcarea utilizatorilor");
    } finally {
      setL(false);
    }
  }

  useEffect(() => {
    loadBase().catch(() => setErr("Nu pot încărca rolurile și permisiunile. Reîncarcă pagina înainte de modificări."));
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const brList = await api.get("/branches/", {
          params: { skip: 0, limit: 200, search: "" },
        });
        setBranches(brList.data?.items || []);
        setBranchListReady(true);
      } catch (e) {
        console.error("Nu pot încărca branches:", e);
        setBranches([]);
      }
    })();
  }, []);

  async function openEdit(userId) {
    setSelectedId(userId);
    setOpen(true);
    setErr("");
    setEdit(null);
    setPermQ("");
    setBranchesReady(false);
    setNotice("");

    try {
      const res = await api.get(`/auth/admin/users/${userId}`);
      // --- BRANCH rights (central) ---
      try {
        const br = await api.get(`/auth/admin/users/${userId}/branches`);
        setBranchIds(
          Array.isArray(br.data?.branch_ids) ? br.data.branch_ids : [],
        );
        setDefaultBranchId(br.data?.default_branch_id ?? null);
        setBranchesReady(true);
      } catch (e2) {
        console.error("Nu pot încărca branch rights:", e2);
        setBranchIds([]);
        setDefaultBranchId(null);
      }
      const d = res.data || {};
      setEdit({
        id: d.id,
        username: d.username,
        is_active: !!d.is_active,
        is_superuser: !!d.is_superuser,
        role_ids: d.role_ids || [],
        role_perm_ids: d.role_perm_ids || [],
        effective_perm_ids: d.effective_perm_ids || [],
        overridesMap: overridesToMap(d.overrides || []),
      });
    } catch (e) {
      setErr(e.response?.data?.detail || "Nu pot încărca drepturile");
    }
  }

  function toggleRole(roleId) {
    setEdit((p) => {
      const has = p.role_ids.includes(roleId);
      return {
        ...p,
        role_ids: has
          ? p.role_ids.filter((x) => x !== roleId)
          : [...p.role_ids, roleId],
      };
    });
  }

  function setPermState(permissionId, state) {
    // state: "inherit" | "allow" | "deny"
    setEdit((p) => {
      const m = { ...(p.overridesMap || {}) };
      const key = String(permissionId);
      if (state === "inherit") delete m[key];
      else if (state === "allow") m[key] = true;
      else if (state === "deny") m[key] = false;
      return { ...p, overridesMap: m };
    });
  }

  function getPermState(permissionId) {
    const key = String(permissionId);
    if (!edit?.overridesMap || !(key in edit.overridesMap)) return "inherit";
    return edit.overridesMap[key] ? "allow" : "deny";
  }

  const rolePermissionIds = useMemo(() => new Set(
    roles.filter(r => edit?.role_ids.includes(r.id)).flatMap(r => r.permission_ids || [])
  ), [roles, edit?.role_ids]);
  function isFromRoles(permissionId) { return rolePermissionIds.has(permissionId); }
  function isEffective(permissionId) {
    if (edit?.is_superuser) return true;
    const state = getPermState(permissionId);
    return state === "allow" || (state === "inherit" && isFromRoles(permissionId));
  }

  async function save() {
    if (!edit || saving || !baseReady) return;
    setSaving(true);
    setErr("");
    try {
      await api.put(`/auth/admin/users/${edit.id}`, {
        is_active: edit.is_active,
        role_ids: edit.role_ids,
        overrides: mapToOverrides(edit.overridesMap),
      });
      setOpen(false);
      await loadUsers();
      setNotice("Drepturile au fost salvate. Utilizatorul trebuie să se deconecteze și să se autentifice din nou pentru a folosi noile drepturi.");
    } catch (e) {
      setErr(e.response?.data?.detail || "Salvare eșuată");
    } finally { setSaving(false); }
  }

  const permsFiltered = useMemo(() => {
    const s = permQ.trim().toLowerCase();
    const list = perms || [];
    if (!s) return list;
    return list.filter((p) => {
      const c = (p.code || "").toLowerCase();
      const d = (p.description || "").toLowerCase();
      const g = groupLabel(p.group || "").toLowerCase();
      return c.includes(s) || d.includes(s) || g.includes(s);
    });
  }, [perms, permQ]);

  const permsByGroup = useMemo(() => {
    const map = new Map();
    for (const p of permsFiltered) {
      const g = p.group || groupFromCode(p.code);
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(p);
    }
    // sort groups
    const groups = Array.from(map.keys()).sort();
    return groups.map((g) => [
      g,
      map.get(g).sort((a, b) => (a.code || "").localeCompare(b.code || "")),
    ]);
  }, [permsFiltered]);

  function toggleGroup(g) {
    setCollapsedGroups((p) => ({ ...p, [g]: !p[g] }));
  }

  return (
    <div className="container py-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h3 className="mb-0">Drepturi utilizatori</h3>
        <div className="d-flex gap-2">
          <a className="btn btn-outline-primary" href="/admin/roluri">Definire roluri</a>
          <button
            type="button"
            className="btn btn-outline-info"
            onClick={() => setShowHelp(true)}
          >
            Ajutor (Ghid Drepturi)
          </button>
          <button
            className="btn btn-outline-secondary"
            onClick={loadUsers}
            disabled={loading}
          >
            Reîncarcă
          </button>
        </div>
      </div>

      {!open && err && <div className="alert alert-danger py-2" role="alert">{err}</div>}
      {!open && notice && <div className="alert alert-success" role="status">{notice}</div>}

      <div className="row g-2 mb-3">
        <div className="col-md-6">
          <input
            className="form-control"
            placeholder="Caută utilizator..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loadUsers()}
          />
        </div>
        <div className="col-md-2">
          <button
            className="btn btn-primary w-100"
            onClick={loadUsers}
            disabled={loading}
          >
            {loading ? "Se caută..." : "Caută"}
          </button>
        </div>
        <div className="col-md-4 d-flex align-items-center text-muted">
          Total: {total}
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th>Utilizator</th>
                <th>Activ</th>
                <th style={{ width: 140 }} />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td className="fw-semibold">{u.username}</td>
                  <td>
                    {u.is_active ? (
                      <span className="badge bg-success">DA</span>
                    ) : (
                      <span className="badge bg-secondary">NU</span>
                    )}
                  </td>
                  <td className="text-end">
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => openEdit(u.id)}
                    >
                      Drepturi
                    </button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={3} className="text-center text-muted py-4">
                    Niciun utilizator
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {open && (
        <div
          className="modal fade show"
          style={{ display: "block", background: "rgba(0,0,0,.35)" }}
          role="dialog"
        >
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title mb-0">
                    Drepturi: {edit?.username || `#${selectedId}`}
                  </h5>
                  <div className="text-muted small">
                    Drepturile din roluri pot fi completate sau restrânse pentru fiecare utilizator.
                  </div>
                </div>
                <button className="btn-close" aria-label="Închide" disabled={saving || savingBranches} onClick={() => setOpen(false)} />
              </div>

              <div className="modal-body">
                {err && <div className="alert alert-danger" role="alert">{err}</div>}
                {notice && <div className="alert alert-success" role="status">{notice}</div>}
                {edit?.is_superuser && <div className="alert alert-warning">Acest cont este superadministrator și are acces complet. Rolurile și excepțiile de mai jos nu îi restrâng accesul.</div>}
                {edit && (!branchesReady || !branchListReady) && <div className="alert alert-warning">Unitățile nu s-au încărcat complet. Salvarea lor este blocată pentru a păstra configurarea existentă.</div>}
                {!edit ? (
                  <div className="text-muted">Se încarcă...</div>
                ) : (
                  <div className="row g-3">
                    {/* Col stânga: user + roluri */}
                    <div className="col-lg-4">
                      <div className="card">
                        <div className="card-body">
                          <div className="form-check form-switch mb-3">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="rights-active"
                              checked={edit.is_active}
                              onChange={(e) =>
                                setEdit((p) => ({
                                  ...p,
                                  is_active: e.target.checked,
                                }))
                              }
                            />
                            <label className="form-check-label" htmlFor="rights-active">
                              Utilizator activ
                            </label>
                          </div>

                          <div className="fw-semibold mb-2">Roluri</div>
                          <div className="d-flex flex-column gap-2">
                            {roles.map((r) => (
                              <label
                                key={r.id}
                                className="border rounded px-2 py-2"
                              >
                                <input
                                  type="checkbox"
                                  className="me-2"
                                  disabled={edit.is_superuser || saving}
                                  checked={edit.role_ids.includes(r.id)}
                                  onChange={() => toggleRole(r.id)}
                                />
                                {roleLabel(r.name)}
                              </label>
                            ))}
                            {roles.length === 0 && (
                              <div className="text-muted">Nu există roluri</div>
                            )}
                          </div>

                          {/* ---------------- BRANCH UI ---------------- */}
                          <hr />
                          <div className="fw-semibold mb-2">Unități</div>
                          <p className="small text-muted">Structuri ale firmei: unitate, departament sau filială.</p>

                          <div className="mb-2">
                            <label className="form-label small mb-1">
                              Unitate implicită
                            </label>
                            <select
                              className="form-select form-select-sm"
                              aria-label="Unitate implicită"
                              value={defaultBranchId ?? ""}
                              onChange={(e) =>
                                setDefaultBranchId(
                                  e.target.value
                                    ? Number(e.target.value)
                                    : null,
                                )
                              }
                            >
                              <option value="">— fără —</option>
                              {branches
                                .filter((b) => branchIds.includes(b.id))
                                .map((b) => (
                                  <option key={b.id} value={b.id}>
                                    {(b.code ? `${b.code} — ` : "") +
                                      (b.name || b.denumire || "")}{" "}
                                    #{b.id}
                                  </option>
                                ))}
                            </select>
                            <div className="form-text">
                              Alege una dintre unitățile bifate mai jos.
                            </div>
                          </div>

                          <div
                            className="border rounded p-2"
                            style={{ maxHeight: 220, overflow: "auto" }}
                          >
                            {branches.map((b) => {
                              const checked = branchIds.includes(b.id);
                              const label =
                                (b.code ? `${b.code} — ` : "") +
                                (b.name || b.denumire || `#${b.id}`);
                              return (
                                <div key={b.id} className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`br_${b.id}`}
                                    checked={checked}
                                    onChange={(e) => {
                                      const next = e.target.checked
                                        ? Array.from(
                                            new Set([...branchIds, b.id]),
                                          )
                                        : branchIds.filter((x) => x !== b.id);

                                      if (
                                        !e.target.checked &&
                                        defaultBranchId === b.id
                                      ) {
                                        setDefaultBranchId(null);
                                      }

                                      setBranchIds(next);
                                    }}
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor={`br_${b.id}`}
                                  >
                                    {label}{" "}
                                    <span className="text-muted">#{b.id}</span>
                                  </label>
                                </div>
                              );
                            })}
                            {!branches.length && (
                              <div className="text-muted small">
                                Nu există unități disponibile.
                              </div>
                            )}
                          </div>

                          <button
                            className="btn btn-sm btn-outline-primary w-100 mt-2"
                            disabled={!edit || savingBranches || !branchesReady || !branchListReady}
                            onClick={async () => {
                              if (!edit) return;
                              setSavingBranches(true);
                              setErr("");
                              try {
                                await api.put(
                                  `/auth/admin/users/${edit.id}/branches`,
                                  {
                                    default_branch_id: defaultBranchId,
                                    branch_ids: branchIds,
                                  },
                                );
                                setNotice("Unitățile au fost salvate. Utilizatorul trebuie să se deconecteze și să se autentifice din nou.");
                              } catch (e) {
                                setErr(
                                  e.response?.data?.detail ||
                                    "Nu pot salva unitățile",
                                );
                              } finally {
                                setSavingBranches(false);
                              }
                            }}
                          >
                            Salvează unitățile
                          </button>
                          {/* ---------------- END BRANCH UI ---------------- */}

                          <hr />

                          <div className="small text-muted">
                            <div>
                              Permisiuni din roluri:{" "}
                              <b>{rolePermissionIds.size}</b>
                            </div>
                            <div>
                              Excepții individuale:{" "}
                              <b>
                                {Object.keys(edit.overridesMap || {}).length}
                              </b>
                            </div>
                            <div>
                              Acces permis: <b>{perms.filter(p => isEffective(p.id)).length}</b>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Col dreapta: perms */}
                    <div className="col-lg-8">
                      <div className="d-flex gap-2 mb-2">
                        <input
                          className="form-control"
                          placeholder="Caută dreptul (cod, descriere, grup)..."
                          value={permQ}
                          onChange={(e) => setPermQ(e.target.value)}
                        />
                        <button
                          className="btn btn-outline-secondary"
                          onClick={() => setPermQ("")}
                          type="button"
                        >
                          Golește
                        </button>
                      </div>

                      <div className="d-flex align-items-center justify-content-between text-muted small mb-2">
                        <div>
                          Legendă:{" "}
                          <span className="badge bg-light text-dark me-1">
                            Din rol
                          </span>
                          <span className="badge bg-success me-1">Acces permis</span>
                          <span className="badge bg-warning text-dark">Interzice</span>
                        </div>
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 text-decoration-none"
                          onClick={() => setShowHelp(true)}
                        >
                          Cum funcționează? (Ajutor)
                        </button>
                      </div>

                      <div className="alert alert-light border small py-2">
                        <div><strong>Din roluri</strong>: se păstrează dreptul acordat de rolurile bifate. Dacă niciun rol nu îl acordă, accesul lipsește.</div>
                        <div><strong>Permite</strong>: adaugă dreptul doar acestui utilizator. <strong>Interzice</strong>: blochează dreptul, chiar dacă un rol îl acordă.</div>
                        <div><strong>Acces permis</strong>: rezultatul final al alegerilor curente, actualizat imediat. Apasă <strong>Salvează</strong> pentru aplicare; utilizatorul se autentifică din nou.</div>
                      </div>
                      {permsByGroup.map(([g, list]) => {
                        const collapsed = !!collapsedGroups[g];
                        return (
                          <div className="card mb-2" key={g}>
                            <div className="card-header d-flex justify-content-between align-items-center">
                              <button
                                className="btn btn-sm btn-link text-decoration-none"
                                onClick={() => toggleGroup(g)}
                                type="button"
                              >
                                {collapsed ? "▶" : "▼"} {groupLabel(g)}{" "}
                                <span className="text-muted">
                                  ({list.length})
                                </span>
                              </button>
                              <div className="text-muted small">
                                {collapsed ? "" : "Din roluri / Permite / Interzice"}
                              </div>
                            </div>

                            {!collapsed && (
                              <div className="card-body p-2">
                                <div className="table-responsive">
                                  <table className="table table-sm mb-0 align-middle">
                                    <thead className="table-light">
                                      <tr>
                                        <th>Cod</th>
                                        <th>Descriere</th>
                                        <th style={{ width: 170 }}>Stare</th>
                                        <th style={{ width: 140 }}>Rezultat</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {list.map((p) => {
                                        const state = getPermState(p.id);
                                        const fromRoles = isFromRoles(p.id);
                                        const effective = isEffective(p.id);
                                        const deny = state === "deny";
                                        return (
                                          <tr key={p.id}>
                                            <td className="fw-semibold">
                                              {p.code}
                                            </td>
                                            <td className="text-muted">
                                              {p.description || ""}
                                            </td>
                                            <td>
                                              <select
                                                className="form-select form-select-sm"
                                                aria-label={`Stare: ${p.description || p.code}`}
                                                disabled={edit.is_superuser || saving}
                                                value={state}
                                                onChange={(e) =>
                                                  setPermState(
                                                    p.id,
                                                    e.target.value,
                                                  )
                                                }
                                              >
                                                <option value="inherit">
                                                  Din roluri
                                                </option>
                                                <option value="allow">
                                                  Permite
                                                </option>
                                                <option value="deny">
                                                  Interzice
                                                </option>
                                              </select>
                                            </td>
                                            <td>
                                              {fromRoles && (
                                                <span className="badge bg-light text-dark me-1">
                                                  Din rol
                                                </span>
                                              )}
                                              {effective && (
                                                <span className="badge bg-success me-1">
                                                  Acces permis
                                                </span>
                                              )}
                                              {!effective && !deny && <span className="badge bg-secondary">Fără acces</span>}
                                              {deny && !edit.is_superuser && (
                                                <span className="badge bg-warning text-dark">
                                                  Interzice
                                                </span>
                                              )}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                      {list.length === 0 && (
                                        <tr>
                                          <td
                                            colSpan={4}
                                            className="text-center text-muted py-3"
                                          >
                                            Nicio permisiune
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {permsByGroup.length === 0 && (
                        <div className="text-muted">Nu există permisiuni.</div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setOpen(false)}
                >
                  Renunță
                </button>
                <button
                  className="btn btn-primary"
                  onClick={save}
                  disabled={!edit || saving || !baseReady}
                >
                  {saving ? "Se salvează..." : "Salvează"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Help Modal */}
      {showHelp && (
        <div
          className="modal fade show"
          style={{ display: "block", background: "rgba(0,0,0,.5)", zIndex: 1060 }}
          role="dialog"
        >
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header bg-light">
                <h5 className="modal-title fw-bold">Ghid Drepturi și Permisiuni</h5>
                <button className="btn-close" aria-label="Închide ajutorul" onClick={() => setShowHelp(false)} />
              </div>
              <div className="modal-body p-4 text-start">
                <div className="alert alert-info">Drepturile utilizatorilor se stabilesc prin roluri și excepții. După salvare, utilizatorul se deconectează și se autentifică din nou.</div>
                <section className="mb-4">
                  <h6 className="fw-bold text-primary mb-3">1. Cum se determină drepturile unui utilizator?</h6>
                  <p>
                    Fiecare acțiune securizată din sistem are asociat un cod unic de permisiune (ex: <code>BON_READ</code> pentru citirea bonurilor de consum).
                    Dreptul final al utilizatorului este calculat combinând <strong>Rolurile</strong> atribuite cu <strong>excepțiile</strong> specifice (pe utilizator).
                  </p>
                </section>

                <section className="mb-4">
                  <h6 className="fw-bold text-primary mb-3">2. Opțiunile din coloana "Stare" (Excepții individuale)</h6>
                  <div className="table-responsive">
                    <table className="table table-bordered table-sm text-start">
                      <thead className="table-light">
                        <tr>
                          <th>Valoare</th>
                          <th>Descriere și Funcționare</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="fw-semibold text-secondary">Din roluri (Implicit)</td>
                          <td>
                            Moștenește permisiunea din rolurile bifate în stânga. 
                            Dacă cel puțin un rol conține această permisiune, utilizatorul o va primi. 
                            Dacă niciun rol nu o are, utilizatorul nu va avea dreptul respectiv.
                          </td>
                        </tr>
                        <tr>
                          <td className="fw-semibold text-success">Permite</td>
                          <td>
                            Forțează activarea permisiunii pentru acest utilizator, indiferent de roluri. 
                            Chiar dacă niciunul din rolurile lui nu îi acordă dreptul, utilizatorul îl va avea.
                          </td>
                        </tr>
                        <tr>
                          <td className="fw-semibold text-danger">Interzice</td>
                          <td>
                            Forțează dezactivarea permisiunii pentru acest utilizator, indiferent de roluri. 
                            Chiar dacă un rol activ i-ar fi oferit acest drept, acesta va fi blocat. 
                            <strong>(Interzice are prioritate maximă peste orice rol)</strong>.
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </section>

                <section className="mb-4">
                  <h6 className="fw-bold text-primary mb-3">3. Semnificația indicatorilor </h6>
                  <ul className="list-group list-group-flush text-start">
                    <li className="list-group-item d-flex align-items-start px-0">
                      <span className="badge bg-light text-dark me-3 mt-1" style={{ minWidth: "80px" }}>Din rol</span>
                      <div>
                        Indică faptul că această permisiune este acordată automat prin intermediul unuia sau mai multor roluri selectate pentru utilizator.
                      </div>
                    </li>
                    <li className="list-group-item d-flex align-items-start px-0">
                      <span className="badge bg-success me-3 mt-1" style={{ minWidth: "80px" }}>Acces permis</span>
                      <div>
                        <strong>Dreptul activ!</strong> Utilizatorul are efectiv acces la această permisiune. Se calculează astfel: 
                        <br />
                        <span className="text-muted small"><code>(Are în Roluri SAU este setat explicit pe Permite) ȘI NU este setat pe Interzice</code></span>.
                      </div>
                    </li>
                    <li className="list-group-item d-flex align-items-start px-0">
                      <span className="badge bg-warning text-dark me-3 mt-1" style={{ minWidth: "80px" }}>Interzice</span>
                      <div>
                        Utilizatorul are o regulă explicită de interzicere setată pe contul său, blocând această acțiune chiar dacă ar fi moștenit-o dintr-un rol.
                      </div>
                    </li>
                  </ul>
                </section>

                <section>
                  <h6 className="fw-bold text-primary mb-2">4. Exemple Practice</h6>
                  <div className="bg-light p-3 rounded border text-start">
                    <ul className="mb-0 ps-3">
                      <li className="mb-2">
                        Utilizatorul are rolul <strong>Utilizator</strong> (care deține permisiunea <code>BON_READ</code>). 
                        Dacă starea este lăsată pe <strong>Din roluri</strong>, el va avea indicatorii <span className="badge bg-light text-dark">Din rol</span> și <span className="badge bg-success">Acces permis</span> (are acces).
                      </li>
                      <li className="mb-2">
                        Dorim să blocăm doar accesul la bonuri pentru acest utilizator, dar să îi lăsăm restul rolului: 
                        Schimbăm starea pentru <code>BON_READ</code> în <strong>Interzice</strong>. 
                        El va păstra indicatorul <span className="badge bg-light text-dark">Din rol</span> (moștenit din rol), dar <span className="badge bg-success">Acces permis</span> va dispărea și va apărea <span className="badge bg-warning text-dark">Interzice</span> (nu are acces).
                      </li>
                      <li>
                        Utilizatorul nu are niciun rol care să îi dea permisiunea <code>STOC_WRITE</code>, dar are nevoie de ea. 
                        Schimbăm starea în <strong>Permite</strong>. Va apărea direct indicatorul <span className="badge bg-success">Acces permis</span>, fără cel de <span className="badge bg-light text-dark">Din rol</span>.
                      </li>
                    </ul>
                  </div>
                </section>
              </div>
              <div className="modal-footer bg-light">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowHelp(false)}
                >
                  Am înțeles
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
